import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../assets/user_context";
import * as ROUTES from "../constants/routes";
import dayjs from "dayjs";
import { tracedCallable } from "../utilities/tracedCallable";
import LoadingGuard from "./loading_guard";
import SubscriptionBanner from "../components/SubscriptionBanner";
import { checkTrialStatusWithCache } from "./subscription_cache.js";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

export const openBillingPortal = ({ org, user, authHook, setLoaded }) => {
  console.log("🔄 openBillingPortal called for user:", user?.uid, "org:", org);

  if (!user?.uid || !org) {
    console.error("❌ Missing user or org in openBillingPortal");
    setLoaded?.(true);
    return;
  }

  if (!authHook?.functions) {
    console.error("❌ Missing authHook.functions in openBillingPortal");
    setLoaded?.(true);
    return;
  }

  setLoaded?.(false);
  const createPortalSession = tracedCallable(
    authHook.functions,
    "createPortalSession",
  );

  createPortalSession({ UID: user.uid, orgID: org })
    .then((result) => {
      console.log("✅ createPortalSession response:", result.data);
      if (result.data?.url) {
        console.log("🔄 Redirecting to Stripe billing portal:", result.data.url);
        window.location.href = result.data.url;
      } else {
        console.error("❌ No URL in portal session response");
        setLoaded?.(true);
      }
    })
    .catch((error) => {
      console.error("❌ Error creating billing portal session:", error);
      setLoaded?.(true);
    });
};

// New function that handles both billing portal and pricing table scenarios
export const handleSubscriptionClick = ({
  org,
  user,
  authHook,
  setLoaded,
  setOpenSubscribe,
}) => {
  console.log("🔄 handleSubscriptionClick called for user:", user?.uid, "org:", org);

  if (!user?.uid || !org) {
    console.error("❌ Missing user or org in handleSubscriptionClick");
    setOpenSubscribe?.(true);
    return;
  }

  if (!authHook?.functions) {
    console.error("❌ Missing authHook.functions in handleSubscriptionClick");
    setOpenSubscribe?.(true);
    return;
  }

  setLoaded?.(false);
  const handleSubscriptionManagement = tracedCallable(
    authHook.functions,
    "handleSubscriptionManagement",
  );

  handleSubscriptionManagement({ userID: user.uid, orgID: org })
    .then((result) => {
      console.log("✅ handleSubscriptionManagement response:", result.data);
      const { action, session, message } = result.data;

      if (action === "openBillingPortal") {
        // User has Stripe customer - redirect to billing portal
        console.log("🔄 Redirecting to Stripe billing portal:", session?.url);
        if (session?.url) {
          window.location.href = session.url;
        } else {
          console.error("❌ No session URL returned from billing portal");
          setLoaded?.(true);
          setOpenSubscribe?.(true);
        }
      } else if (action === "showPricingTable") {
        // User needs to create subscription - show pricing table
        console.log("📝 User needs to create subscription - showing pricing table");
        setLoaded?.(true);
        setOpenSubscribe?.(true);
      } else if (action === "notBillingOwner") {
        // User is not the billing owner for this managed org
        console.log("🚫 User is not billing owner:", message);
        setLoaded?.(true);
        alert(message || "You are not the billing owner for this organization. Contact the subscription administrator to manage billing.");
      } else if (action === "orgSuspended") {
        // Organization is suspended due to over-capacity
        console.log("🚫 Org is suspended:", message);
        setLoaded?.(true);
        alert(message || "This organization's subscription is suspended due to over-capacity. Please resolve this in Subscription Management.");
      } else {
        console.error("❌ Unknown action from handleSubscriptionManagement:", action);
        setLoaded?.(true);
        setOpenSubscribe?.(true);
      }
    })
    .catch((error) => {
      console.error("❌ Error handling subscription management:", error);
      setLoaded?.(true);
      // Fallback to showing pricing table if there's an error
      setOpenSubscribe?.(true);
    });
};

export const withAuthentication = (Component) => {
  return (props) => {
    return (
      <LoadingGuard requireAuth={true} requireOrg={true}>
        <Component {...props} />
      </LoadingGuard>
    );
  };
};

const withSubscriptionProtection = (Component) => {
  return (props) => {
    const {
      user,
      orgData,
      subscriptionData,
      setOpenSubscribe,
      loaded,
      freeTrialNotified,
      setFreeTrialNotified,
      setOpenTrialNotification,
      authHook,
      org,
    } = useContext(UserContext);

    // Function to check if subscription-protected actions should be allowed
    const allowFunctionWithSub = () => {
      const hasAccess =
        subscriptionData?.subscriptionCurrent || subscriptionData?.freeTrial;

      if (!hasAccess) {
        // Open the subscription modal if they don't have access
        setOpenSubscribe(true);
        return false;
      }

      return true;
    };

    useEffect(() => {
      // Check for subscription status only if loading is finished and only when org changes
      // This prevents redundant calls from multiple protected components
      if (
        loaded &&
        orgData &&
        orgData.onboarded &&
        user &&
        org &&
        authHook?.functions
      ) {
        // Use cached approach to check trial status (24-hour cache)
        const checkFreeTrialStatus = async () => {
          try {
            const result = await checkTrialStatusWithCache(org, authHook);
            debugLog("Free trial status check (cached):", result);

            // Show free trial notification if appropriate
            if (
              result.freeTrial &&
              !freeTrialNotified &&
              result.daysSinceCreation === 0
            ) {
              setOpenTrialNotification(true);
              setFreeTrialNotified(true);
            }
          } catch (error) {
            console.error("Error checking free trial status:", error);
          }
        };

        checkFreeTrialStatus();
      }
    }, [
      org, // Only trigger when org changes - prevents redundant calls
      loaded, // And when loading completes
      orgData?.onboarded, // And when org onboarding status is known
      // Removed user, freeTrialNotified, authHook from dependencies to prevent excessive calls
    ]);

    return (
      <LoadingGuard requireAuth={true} requireOrg={true}>
        <Component {...props} allowFunctionWithSub={allowFunctionWithSub} />
        <SubscriptionBanner />
      </LoadingGuard>
    );
  };
};

export default withSubscriptionProtection;
